import React from "react";

import { Button } from "@javascript/components/ui/button";
import { Input } from "@javascript/components/ui/input";
import { cn } from "@javascript/components/utils";

export type PlaceMatch = {
  id: string;
  description: string;
};

export type ChosenPlace = {
  description: string;
  latitude: string;
  longitude: string;
};

export type PlaceSearchClient = {
  search(query: string): Promise<PlaceMatch[]>;
  select(id: string): Promise<ChosenPlace>;
};

type GooglePlacePrediction = {
  placeId: string;
  text: { toString(): string };
  toPlace(): GooglePlace;
};

type GooglePlace = {
  location?: { lat(): number; lng(): number };
  fetchFields(options: { fields: string[] }): Promise<void>;
};

type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(options: {
      input: string;
      sessionToken: object;
    }): Promise<{ suggestions: Array<{ placePrediction?: GooglePlacePrediction }> }>;
  };
};

type MapCoordinates = {
  lat: number;
  lng: number;
};

type GoogleMapsLibrary = {
  Map: new (
    element: HTMLElement,
    options: {
      center: MapCoordinates;
      zoom: number;
      mapId?: string;
      fullscreenControl: boolean;
      mapTypeControl: boolean;
      streetViewControl: boolean;
      gestureHandling: string;
    },
  ) => { setCenter(coordinates: MapCoordinates): void };
};

type GoogleMarker = {
  addListener(
    event: "dragend",
    callback: (event: { latLng?: { lat(): number; lng(): number } }) => void,
  ): { remove(): void };
  map: object | null;
  position: MapCoordinates;
};

type GoogleMarkerLibrary = {
  AdvancedMarkerElement: new (options: {
    map: object;
    position: MapCoordinates;
    gmpDraggable: boolean;
    title: string;
    content: HTMLElement;
  }) => GoogleMarker;
  PinElement: new (options: {
    background: string;
    borderColor: string;
    glyphColor: string;
  }) => { element: HTMLElement };
};

// The map's marker is drawn by Google outside our component tree, so it can't
// take Tailwind classes — read the live design tokens instead of duplicating
// their values here.
function venuePin(markerLibrary: GoogleMarkerLibrary): HTMLElement {
  const tokens = getComputedStyle(document.documentElement);
  const background = tokens.getPropertyValue("--primary").trim();
  const contrast = tokens.getPropertyValue("--primary-foreground").trim();

  return new markerLibrary.PinElement({
    background,
    borderColor: contrast,
    glyphColor: contrast,
  }).element;
}

declare global {
  interface Window {
    google?: {
      maps: {
        importLibrary(name: "places"): Promise<GooglePlacesLibrary>;
        importLibrary(name: "maps"): Promise<GoogleMapsLibrary>;
        importLibrary(name: "marker"): Promise<GoogleMarkerLibrary>;
      };
    };
  }
}

// Cached only while pending or fulfilled: a rejected load is forgotten so the
// next search retries the script instead of replaying the original failure for
// the rest of the page's life.
let googleMapsPromise: Promise<void> | undefined;
let googleMapsCallbackSeq = 0;

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (googleMapsPromise) return googleMapsPromise;

  const pending = new Promise<void>((resolve, reject) => {
    const callbackName = `weddangGoogleMapsReady${(googleMapsCallbackSeq += 1)}`;
    const script = document.createElement("script");

    const cleanUp = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
    };

    Object.assign(window, {
      [callbackName]: () => {
        cleanUp();
        resolve();
      },
    });
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      cleanUp();
      script.remove();
      reject(new Error("Google Maps failed to load"));
    };
    document.head.appendChild(script);
  });

  let loader: Promise<void>;
  loader = pending.catch((error) => {
    if (googleMapsPromise === loader) googleMapsPromise = undefined;
    throw error;
  });

  googleMapsPromise = loader;
  return loader;
}

export function createPlaceSearchClient(apiKey: string): PlaceSearchClient {
  // One Autocomplete session token spans every keystroke's suggestion request
  // and the Place Details fetch for the chosen match, so Google bills the whole
  // pick as a single session. The first details fetch retires the token,
  // successful or not, because Google considers that session complete.
  let sessionToken: object | undefined;
  const predictions = new Map<string, GooglePlacePrediction>();

  async function placesLibrary() {
    await loadGoogleMaps(apiKey);
    return window.google!.maps.importLibrary("places");
  }

  return {
    async search(query) {
      const places = await placesLibrary();
      sessionToken ||= new places.AutocompleteSessionToken();
      const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken,
      });

      return response.suggestions.flatMap((suggestion) => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return [];

        predictions.set(prediction.placeId, prediction);
        return [{ id: prediction.placeId, description: prediction.text.toString() }];
      });
    },

    async select(id) {
      const prediction = predictions.get(id);
      if (!prediction) throw new Error("Place prediction is no longer available");

      const place = prediction.toPlace();
      try {
        await place.fetchFields({ fields: ["location"] });
        if (!place.location) throw new Error("Place has no location");

        return {
          description: prediction.text.toString(),
          latitude: String(place.location.lat()),
          longitude: String(place.location.lng()),
        };
      } finally {
        // The first Place Details fetch ends the autocomplete session, even if
        // it yields no usable coordinates. Never reuse that token for a retry.
        sessionToken = undefined;
        predictions.clear();
      }
    },
  };
}

type VenueMapHandle = {
  setLocation(coordinates: MapCoordinates): void;
  destroy(): void;
};

export type VenueMapClient = {
  create(
    element: HTMLElement,
    coordinates: MapCoordinates,
    onDragEnd: (coordinates: MapCoordinates) => void,
  ): Promise<VenueMapHandle>;
};

export function createVenueMapClient(apiKey: string, mapId?: string): VenueMapClient {
  return {
    async create(element, coordinates, onDragEnd) {
      await loadGoogleMaps(apiKey);
      const [maps, markerLibrary] = await Promise.all([
        window.google!.maps.importLibrary("maps"),
        window.google!.maps.importLibrary("marker"),
      ]);
      const map = new maps.Map(element, {
        center: coordinates,
        zoom: 16,
        mapId,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: "cooperative",
      });
      const marker = new markerLibrary.AdvancedMarkerElement({
        map,
        position: coordinates,
        gmpDraggable: true,
        title: "Drag to fine-tune the venue location",
        content: venuePin(markerLibrary),
      });
      const dragListener = marker.addListener("dragend", (event) => {
        const location = event.latLng;
        if (!location) return;

        onDragEnd({ lat: location.lat(), lng: location.lng() });
      });

      return {
        setLocation(nextCoordinates) {
          map.setCenter(nextCoordinates);
          marker.position = nextCoordinates;
        },
        destroy() {
          dragListener.remove();
          marker.map = null;
        },
      };
    },
  };
}

type SearchStatus = "idle" | "searching" | "empty" | "error";

export type VenueCoordinateField = {
  name: string;
  value: string | null;
};

const STATUS_MESSAGES: Record<SearchStatus, string> = {
  idle: "",
  searching: "Searching…",
  empty: "No places found.",
  error: "Place search is unavailable. Try again.",
};

type VenueMapFieldProps = {
  apiKey: string;
  mapId?: string;
  searchId?: string;
  latitudeField: VenueCoordinateField;
  longitudeField: VenueCoordinateField;
  client?: PlaceSearchClient;
  mapClient?: VenueMapClient;
};

export function VenueMapField({
  apiKey,
  mapId,
  searchId = "venue-map-search",
  latitudeField,
  longitudeField,
  client: suppliedClient,
  mapClient: suppliedMapClient,
}: VenueMapFieldProps) {
  const [client] = React.useState(() => suppliedClient ?? createPlaceSearchClient(apiKey));
  const [mapClient] = React.useState(() => suppliedMapClient ?? createVenueMapClient(apiKey, mapId));
  const [query, setQuery] = React.useState("");
  const [matches, setMatches] = React.useState<PlaceMatch[]>([]);
  const [dismissed, setDismissed] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [status, setStatus] = React.useState<SearchStatus>("idle");
  const [latitude, setLatitude] = React.useState(latitudeField.value ?? "");
  const [longitude, setLongitude] = React.useState(longitudeField.value ?? "");
  const [chosenDescription, setChosenDescription] = React.useState<string | null>(null);
  // Only the newest selection may write coordinates: a slower earlier pick must
  // not overwrite the place the host settled on.
  const selectionSeq = React.useRef(0);
  const mapElementRef = React.useRef<HTMLDivElement>(null);
  const mapHandleRef = React.useRef<VenueMapHandle | null>(null);

  const baseId = React.useId();
  const listboxId = `${baseId}-matches`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const listboxOpen = matches.length > 0 && !dismissed;

  React.useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setMatches([]);
      setDismissed(false);
      setActiveIndex(-1);
      setStatus("idle");
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setStatus("searching");

      try {
        const results = await client.search(trimmedQuery);
        if (controller.signal.aborted) return;

        setMatches(results);
        setDismissed(false);
        setActiveIndex(-1);
        setStatus(results.length === 0 ? "empty" : "idle");
      } catch {
        if (!controller.signal.aborted) {
          setMatches([]);
          setDismissed(false);
          setActiveIndex(-1);
          setStatus("error");
        }
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [client, query]);

  async function choosePlace(match: PlaceMatch) {
    const selection = (selectionSeq.current += 1);

    try {
      const place = await client.select(match.id);
      if (selection !== selectionSeq.current) return;

      setLatitude(place.latitude);
      setLongitude(place.longitude);
      setChosenDescription(place.description);
      setStatus("idle");
      setQuery("");
      setMatches([]);
      setActiveIndex(-1);
    } catch {
      if (selection !== selectionSeq.current) return;

      setStatus("error");
    }
  }

  function changeQuery(nextQuery: string) {
    // Typing a new query means the host has moved on from a pending choice.
    // A late Place Details response must not overwrite that newer intent.
    selectionSeq.current += 1;
    setQuery(nextQuery);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      if (!listboxOpen) return;
      event.preventDefault();
      setDismissed(true);
      setActiveIndex(-1);
      return;
    }

    if (event.key === "Enter") {
      // Enter inside the search box must never fall through to the invitation
      // form's implicit submission — the host is searching, not saving.
      if (!query.trim()) return;
      event.preventDefault();
      if (activeIndex >= 0) void choosePlace(matches[activeIndex]);
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    if (matches.length === 0) return;

    event.preventDefault();

    if (!listboxOpen) {
      setDismissed(false);
      setActiveIndex(event.key === "ArrowDown" ? 0 : matches.length - 1);
      return;
    }

    const step = event.key === "ArrowDown" ? 1 : -1;
    setActiveIndex((current) => {
      if (current < 0) return step === 1 ? 0 : matches.length - 1;
      return (current + step + matches.length) % matches.length;
    });
  }

  function clearLocation() {
    // Retire any in-flight selection so a late resolution cannot repopulate
    // the coordinates the host just cleared.
    selectionSeq.current += 1;
    setLatitude("");
    setLongitude("");
    setChosenDescription(null);
    setStatus("idle");
    setQuery("");
    setMatches([]);
    setActiveIndex(-1);
  }

  const mapCoordinates = coordinatesFrom(latitude, longitude);
  const latestMapCoordinates = React.useRef(mapCoordinates);
  latestMapCoordinates.current = mapCoordinates;
  const hasMapCoordinates = mapCoordinates !== null;
  const savedCoordinates = latitude && longitude ? `${latitude}, ${longitude}` : null;

  React.useEffect(() => {
    if (!hasMapCoordinates || !mapElementRef.current) {
      mapHandleRef.current?.destroy();
      mapHandleRef.current = null;
      return;
    }

    let disposed = false;
    void mapClient
      .create(mapElementRef.current, latestMapCoordinates.current!, (coordinates) => {
        selectionSeq.current += 1;
        setLatitude(String(coordinates.lat));
        setLongitude(String(coordinates.lng));
      })
      .then((handle) => {
        if (disposed) {
          handle.destroy();
          return;
        }

        mapHandleRef.current = handle;
        const latestCoordinates = latestMapCoordinates.current;
        if (latestCoordinates) handle.setLocation(latestCoordinates);
      })
      .catch(() => {
        // Place search already exposes its own failure state. A map failure must
        // not discard the coordinates the host selected or previously saved.
      });

    return () => {
      disposed = true;
      mapHandleRef.current?.destroy();
      mapHandleRef.current = null;
    };
  }, [hasMapCoordinates, mapClient]);

  React.useEffect(() => {
    if (mapCoordinates) mapHandleRef.current?.setLocation(mapCoordinates);
  }, [mapCoordinates]);

  return (
    <div
      className="space-y-3"
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDismissed(true);
        setActiveIndex(-1);
      }}
    >
      <Input
        id={searchId}
        role="combobox"
        aria-label="Search for a venue"
        aria-autocomplete="list"
        aria-expanded={listboxOpen}
        aria-controls={listboxOpen ? listboxId : undefined}
        aria-activedescendant={listboxOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        value={query}
        onChange={(event) => changeQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search by venue name or address"
      />
      {listboxOpen && (
        <div className="rounded-md border bg-popover text-popover-foreground shadow-sm">
          <ul id={listboxId} role="listbox" aria-label="Venue matches" className="divide-y">
            {matches.map((match, index) => (
              <li
                key={match.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className={cn(
                  "cursor-pointer px-3 py-2 text-left text-sm transition-colors",
                  index === activeIndex && "bg-accent text-accent-foreground",
                )}
                // Keep focus on the combobox so the blur handler does not close
                // the list before the click lands.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => void choosePlace(match)}
              >
                {match.description}
              </li>
            ))}
          </ul>
          <p className="border-t px-3 py-1.5 text-right text-xs text-muted-foreground" translate="no">
            Google Maps
          </p>
        </div>
      )}
      {/* Always mounted so assistive technology observes the region before it
          has anything to announce. */}
      <p
        role="status"
        aria-live="polite"
        className={cn(
          "text-sm empty:sr-only",
          status === "error" ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {STATUS_MESSAGES[status]}
      </p>
      {(chosenDescription || savedCoordinates) && (
        <div className="rounded-md border bg-muted/40">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <p className="text-sm">
              {chosenDescription ?? `Saved location: ${savedCoordinates}`}
            </p>
            <Button type="button" variant="ghost" size="sm" onClick={clearLocation}>
              Clear location
            </Button>
          </div>
          {chosenDescription && (
            <p className="border-t px-3 py-1.5 text-right text-xs text-muted-foreground" translate="no">
              Google Maps
            </p>
          )}
        </div>
      )}
      {mapCoordinates && (
        <div className="space-y-2">
          <div
            ref={mapElementRef}
            role="region"
            aria-label="Venue map"
            className="h-64 w-full overflow-hidden rounded-md border bg-muted sm:h-72"
          />
          <p className="text-sm text-muted-foreground">Drag the pin to the exact venue location.</p>
        </div>
      )}
      <input type="hidden" name={latitudeField.name} value={latitude} />
      <input type="hidden" name={longitudeField.name} value={longitude} />
    </div>
  );
}

function coordinatesFrom(latitude: string, longitude: string): MapCoordinates | null {
  if (!latitude || !longitude) return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}
